import { BRAND_NAME, BRAND_PERSON_NAME, BRAND_SITE_URL, BRAND_AGE, BRAND_CITY } from "@/lib/brandConfig";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { trackBaseTemplateClick } from "@/lib/baseTemplateAnalytics";
import { detectTrafficSource } from "@/lib/detectTrafficSource";
import { getSlackFunnelDiagnosticsExtraPostHog } from "@/lib/posthogAnalytics";
import {
  getCurrencyForCountry,
  getPlansForCurrency,
  isPremiumSpecialOfferActive,
} from "@/config/bioTemplatePremiumPlans";
import {
  getPremiumInvitePaths,
  PREMIUM_INVITE_FLOW_NAME,
} from "@/config/bioTemplatePremiumFlow";
import { isCloudinaryImage, isSameCloudinaryImage, resolveLogoUrl } from "@/lib/cloudinaryUtils";
import { brandDisplayNameFromSiteTitle } from "@/lib/siteBrandFromSettings";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";
import {
  isDualMuxMembershipSurface,
  registerDualMuxMembershipFunnelFromWorkerPostHog,
} from "@/lib/dualMuxPostHogFunnel";
import {
  registerVisitorExperienceContextPostHog,
  trackVisitorExperienceResolvedPostHog,
} from "@/lib/visitorExperiencePostHog";
import { usesSpanishDefaultForSite } from "@/lib/siteGeoPolicy";
import { useCurrentTemplate } from "@/contexts/CurrentTemplateContext";
import { useSEO } from "@/hooks/useSEO";
import { usePortfolioSections } from "@/hooks/usePortfolioSections";
import { usePremiumInviteFreeTelegramPublicUrl } from "@/hooks/usePremiumInviteFreeTelegramPublicUrl";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";
import { useDualVariantTitle } from "@/lib/useDualVariantTitle";
import {
  BESTFRIEND_INSTAGRAM_CONFIG,
  getBestFriendInstagramConfigForCountry,
} from "@/config/bestFriendInstagramConfig";
import { TELEGRAM_GROUP_URL } from "@/config/telegramGroups";
import { getGalleryPhotos } from "@/config/galleryConfig";
import { getHomeVariantForCountry } from "@/config/homeCountryVariants";
import { useEffect, useMemo, useState, lazy, Suspense } from "react";
import { Spinner } from "@heroui/react";

// Development components
import { ArgentinaProvinceAdmin } from "@/components/dev/ArgentinaProvinceAdmin";
import { TemplateLoadingScreen } from "@/components/templates/TemplateLoadingScreen";
import { useLoadingGate } from "@/hooks/useLoadingGate";
import { useTemplateAssetsReady } from "@/hooks/useTemplateAssetsReady";
import {
  hideCtaBlocksDualHome,
  isIntlDualHomeTemplate,
} from "@/lib/dualHomeRouting";

const BaseTemplate = lazy(() =>
  import("@/components/templates/BaseTemplateBlack").then((m) => ({
    default: m.BaseTemplateBlack,
  })),
);
// Import de BaseTemplate.tsx (diseño limpio, el que ve Argentina). Extraído a
// una función nombrada para poder PRECARGAR el chunk apenas detectamos visitante
// AR (ver effect abajo) — así está listo antes de que el splash termine.
const importBaseTemplateMain = () =>
  import("@/components/templates/BaseTemplate").then((m) => ({
    default: m.BaseTemplate,
  }));
const BaseTemplateMain = lazy(importBaseTemplateMain);
const BaseTemplateInternational = lazy(() =>
  import("@/components/templates/BaseTemplateInternational").then((m) => ({
    default: m.BaseTemplateInternational,
  })),
);
const BioLinkTemplate = lazy(() =>
  import("@/components/templates/BioLinkTemplate").then((m) => ({
    default: m.BioLinkTemplate,
  })),
);
const ArgentinaBioTemplate = lazy(() =>
  import("@/components/templates/ArgentinaBioTemplate").then((m) => ({
    default: m.ArgentinaBioTemplate,
  })),
);
const MaintenanceTemplate = lazy(() =>
  import("@/components/templates/MaintenanceTemplate").then((m) => ({
    default: m.MaintenanceTemplate,
  })),
);
const BioTemplatePremium = lazy(() => import("@/components/templates/BioTemplatePremium"));
const BioTemplatePremiumSimple = lazy(
  () => import("@/components/templates/BioTemplatePremiumSimple"),
);
const VideoTemplate = lazy(() =>
  import("@/components/templates/VideoTemplate").then((m) => ({
    default: m.VideoTemplate,
  })),
);
const DualCTAVideoLanding = lazy(() => import("@/pages/DualCTAVideoLanding"));
const USExclusiveLanding = lazy(() => import("@/pages/USExclusiveLanding"));

export const Index = () => {
  const templateSelection = useTemplateSelection();
  const { templateName, country, language, region, showDual, hideCta } = templateSelection;
  const { t } = useTranslation();
  const { setTemplateName } = useCurrentTemplate();

  const premiumSimpleHome = templateName === "BioTemplatePremiumSimple";
  const {
    url: premiumSimpleFreeTelegramUrl,
    publicConfig: premiumInvitePublicConfig,
    ready: premiumSimpleConfigReady,
  } = usePremiumInviteFreeTelegramPublicUrl(premiumSimpleHome);

  const workerEmergencyFlags = useMemo(() => {
    const cfg = getServerTemplateConfig();
    return {
      globalHideHeroCta: cfg?.globalHideHeroCta === true,
      killSwitchActive: cfg?.killSwitchActive === true,
    };
  }, []);

  // Reforzar loading AR: precargar el chunk de BaseTemplate.tsx (BaseTemplateMain)
  // apenas detectamos visitante Argentina. El splash (useLoadingGate +
  // useTemplateAssetsReady) ya se sostiene hasta que el worker inyectó el país
  // (detección AR) + banner + fonts; precargar el chunk garantiza que el
  // componente esté LISTO cuando el splash termina, sin un segundo loading
  // (fallback de Suspense) entre el fade y el primer paint del template.
  useEffect(() => {
    if (usesSpanishDefaultForSite()) {
      void importBaseTemplateMain();
    }
  }, []);

  const dualHomeSuppressed = hideCtaBlocksDualHome(
    hideCta,
    templateName,
    workerEmergencyFlags,
  );

  // When WishlistAdmin sets cta_type="dual", the Worker emits showDual=true but
  // templateName remains the country default (e.g. "BioTemplatePremiumSimple").
  // We resolve the effective name here so PostHog / session replay always reflects
  // the template that actually rendered, not the underlying geo default.
  //
  // HIERARCHY: hideCta=true (GLOBAL_HIDE_HERO_CTA) beats both CountryTemplateAdmin
  // and WishlistAdmin — no dual template promotion when all geo-CTAs are hidden.
  const effectiveTemplateName = useMemo(() => {
    // Explicit admin template overrides son exactos — nunca promovidos a una
    // variante dual. Mantener `DualCTAVideoLanding` acá significa que cuando
    // admin asignó explícito ese template Y no hay showDual, sigue sirviendo
    // el fullscreen (no se intercepta). Si admin asignó VideoTemplate, idem
    // — devolvemos su valor sin promote a algo más.
    if (
      templateName === "DualCTAVideoLanding" ||
      templateName === "USExclusiveLanding" ||
      templateName === "VideoTemplate" ||
      templateName === "MaintenanceTemplate" ||
      templateName === "ArgentinaBioTemplate" ||
      templateName === "BioLinkTemplate"
    ) {
      // Coherente con useDualCta: intl + DualCTAVideoLanding → fullscreen
      // DualCTAVideoLanding en `/` (MuxVideoPlayerWithCTA + variantes A/B/C).
      if (
        isIntlDualHomeTemplate(templateName) &&
        !dualHomeSuppressed &&
        !usesSpanishDefaultForSite() &&
        country.length === 2 &&
        country !== "UNKNOWN"
      ) {
        return templateName;
      }
      return templateName;
    }
    if (dualHomeSuppressed) return templateName; // GLOBAL_HIDE / killSwitch → no dual promotion
    // BaseTemplateInternationalDual → mismo render fullscreen que DualCTAVideoLanding.
    if (templateName === "BaseTemplateInternationalDual") return "DualCTAVideoLanding";
    if (
      showDual &&
      !usesSpanishDefaultForSite() &&
      country.length === 2 &&
      country !== "UNKNOWN"
    ) {
      // Default intl + showDual: promovemos a VideoTemplate (el default
      // recomendado). Esto cubre el caso donde admin tiene
      // templateName=BaseTemplate (custom override) Y cta_type=dual — el
      // VideoTemplate gana SOLO si BaseTemplate no era una intención explícita
      // del admin. Actualmente NO distinguimos override-vs-default desde el
      // front, así que esta rama puede pisar BaseTemplate overrides para intl
      // con showDual. Para respetar BaseTemplate intencional, admin debe
      // dejar cta_type vacío (no dual) — entonces showDual=false y caemos a
      // `return templateName` (BaseTemplate sin dual).
      return "VideoTemplate";
    }
    // AR dual CTA: admin configura cta_type="dual" para AR scope → layout
    // principal. Gate `templateName === "BaseTemplate"` porque el dual hero
    // path SOLO aplica cuando el visitor venía a BaseTemplate (regla canónica
    // del audit). Si admin asignó VideoTemplate o DualCTAVideoLanding al
    // visitor AR, esos templates tienen su propio flow dual completo y NO
    // queremos interceptar con BaseTemplateArDual.
    if (showDual && country === "AR" && templateName === "BaseTemplate") {
      return "BaseTemplateArDual";
    }
    return templateName;
  }, [templateName, showDual, country, dualHomeSuppressed]);

  // PostHog + session replay: expose template for pageview/route_change (all templates)
  useEffect(() => {
    setTemplateName(effectiveTemplateName ?? null);
    return () => setTemplateName(null);
  }, [effectiveTemplateName, setTemplateName]);

  // Super-props de experiencia (template efectivo + dual + cascade) para TODAS
  // las superficies — no solo dual/Mux. Permite filtrar en PostHog qué ve cada
  // visitor (state_us, city, template_source, dual_variant_id, …).
  //
  // SessionIngress suele disparar `visitor_experience_resolved` con el seed del
  // worker; aquí re-fire con force cuando Index ya tiene el template efectivo
  // (puede diferir por city/state cascade SPA).
  useEffect(() => {
    if (!effectiveTemplateName) return;
    const dualSurface = getServerTemplateConfig()?.dualSurface ?? null;
    registerVisitorExperienceContextPostHog({
      effectiveTemplate: effectiveTemplateName,
      dualSurface,
    });
    // force promotes past SessionIngress seed only when fingerprint differs
    // (template/surface/cascade) — no double-fire spam on remount.
    trackVisitorExperienceResolvedPostHog({
      effectiveTemplate: effectiveTemplateName,
      dualSurface,
      force: true,
    });
    if (isDualMuxMembershipSurface(effectiveTemplateName)) {
      registerDualMuxMembershipFunnelFromWorkerPostHog(effectiveTemplateName);
    }
  }, [effectiveTemplateName]);

  // Título distintivo por variante del Dual CTA (solo aplica al AR-dual aquí;
  // el global-dual setea su propio título dentro de DualCTAVideoLanding).
  useDualVariantTitle(
    effectiveTemplateName === "BaseTemplateArDual" ? "ar" : null,
  );

  // Site settings from admin (BaseTemplate: header URL, title, description, gallery)
  const { settings: siteSettings, isLoading: siteSettingsLoading } = useSiteSettings();
  // Portfolio sections from admin - solo para ArgentinaBioTemplate/BioLinkTemplate (BaseTemplate usa solo site settings)
  const {
    allImageUrls: portfolioImageUrls,
    isLoading: portfolioLoading,
    showPortfolioNavLink,
  } = usePortfolioSections();

  // Audiovisual nav: only show the link when the admin actually has videos loaded.
  // Default false so the link never appears unless /api/audiovisual confirms items exist
  // (no data, no toggle, or a failed request → link stays hidden).
  //
  // Revalidate triggers (mismo patrón que Audiovisual.tsx para coherencia):
  //   - mount inicial
  //   - storage event con key "audiovisual_updated" (admin guardó en otra tab)
  //   - visibilitychange → visible (tab volvió a foreground tras edit en otro)
  //   - custom event "audiovisual-updated" (admin guardó en esta misma tab)
  //
  // Sin estos triggers, el admin que apaga `showInFront` ve el link seguir
  // visible hasta hard reload — el efecto de "activar/desactivar" no se siente real.
  const [showAudiovisualNavLink, setShowAudiovisualNavLink] = useState(false);
  useEffect(() => {
    let cancelled = false;

    const fetchVisibility = () => {
      fetch("/api/audiovisual", { cache: "no-store" })
        .then((r) => r.ok ? r.json() : { items: [], showInFront: false })
        .then((data: { items?: unknown[]; showInFront?: boolean }) => {
          if (!cancelled) {
            const hasItems = (data.items?.length ?? 0) > 0;
            const visible = hasItems && (data.showInFront !== false);
            setShowAudiovisualNavLink(visible);
          }
        })
        .catch(() => { if (!cancelled) setShowAudiovisualNavLink(false); });
    };

    fetchVisibility();

    if (typeof window === "undefined" || typeof document === "undefined") {
      return () => { cancelled = true; };
    }
    const onStorage = (e: StorageEvent) => {
      if (e.key === "audiovisual_updated") fetchVisibility();
    };
    const onLocalUpdate = () => { fetchVisibility(); };
    const onVisibility = () => {
      if (document.visibilityState === "visible") fetchVisibility();
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("audiovisual-updated", onLocalUpdate);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("audiovisual-updated", onLocalUpdate);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  // Timeout de seguridad: si después de 8 segundos aún está cargando, forzar render
  const [forceRender, setForceRender] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (siteSettingsLoading || portfolioLoading) {
        console.warn('[Index] Loading timeout reached, forcing render with defaults');
        setForceRender(true);
      }
    }, 8000);
    return () => clearTimeout(timer);
  }, [siteSettingsLoading, portfolioLoading]);

  // Enhanced SEO configuration with region-specific optimization
  // Always prefer admin-configured siteSettings.description (editable from admin DB).
  // Env-var fallbacks use BRAND_CITY / BRAND_AGE so no city is ever hardcoded here.
  const isBaseTemplate = !["ArgentinaBioTemplate", "BioLinkTemplate", "MaintenanceTemplate", "BioTemplatePremium", "BioTemplatePremiumSimple"].includes(templateName ?? "");
  const brandCity = BRAND_CITY || "Argentina";
  const ageFragment = BRAND_AGE ? `, ${BRAND_AGE} años` : "";
  const latamDescFallback = `Modelo y creadora de contenido para redes sociales${ageFragment}, ${brandCity}. Campañas, colaboraciones y comunidad.`;
  const latamOgFallback = `Modelo y creadora de contenido${ageFragment ? ` · ${BRAND_AGE} años` : ""} · ${brandCity}. Campañas y colaboraciones.`;
  const latamTitleFallback = `${BRAND_PERSON_NAME} | Modelo y creadora de contenido — ${brandCity}`;
  // For all LATAM templates: siteSettings.description (admin DB) takes priority over env-var fallback
  const latamDesc = siteSettings.description || latamDescFallback;
  const latamTitle = siteSettings.title || latamTitleFallback;
  const seoConfig = {
    title: region === "US"
      ? `${BRAND_PERSON_NAME} | Join My Community - Premium Content & Exclusive Access`
      : region === "SPAIN"
      ? `${BRAND_PERSON_NAME} | Únete a Mi Comunidad - Contenido Premium y Acceso Exclusivo`
      : isBaseTemplate
      ? siteSettings.title
      : `${BRAND_PERSON_NAME} | Tiktoker y Modelo Argentina — Buenos Aires`,
    description: region === "US"
      ? `Join ${BRAND_PERSON_NAME}'s exclusive community! Get access to premium Instagram content, join our Telegram group, and connect directly. Your gateway to exclusive lifestyle content and personal interaction.`
      : region === "SPAIN"
      ? `¡Únete a la comunidad exclusiva de ${BRAND_PERSON_NAME}! Accede a contenido premium de Instagram, únete a nuestro grupo de Telegram y conecta directamente. Tu puerta de entrada al contenido exclusivo de lifestyle e interacción personal.`
      : isBaseTemplate
      ? siteSettings.description
      : `Tiktoker y modelo argentina de Buenos Aires. Creadora de contenido, ${BRAND_AGE} años. Campañas, colaboraciones y acceso a contenido exclusivo.`,
    keywords: region === "US"
      ? `${BRAND_PERSON_NAME}, premium content, exclusive access, Instagram content, Telegram group, bio link, content creator, lifestyle, community, exclusive content`
      : region === "SPAIN"
      ? `${BRAND_PERSON_NAME}, contenido premium, acceso exclusivo, contenido Instagram, grupo Telegram, bio link, creadora contenido, lifestyle, comunidad, contenido exclusivo, España`
      : `${BRAND_PERSON_NAME}, tiktoker argentina, modelo argentina, creadora de contenido, influencer argentina, Buenos Aires, CABA, content creator argentina, modelo instagram argentina`,
    ogImage: region === "US" 
      ? "https://imagedelivery.net/bfb1a679ca6faf7c85a4350b05feb469/1b0c6841-2a9f-45dd-a3a2-e2a3e60f6600/public"
      : region === "SPAIN"
      ? "https://imagedelivery.net/bfb1a679ca6faf7c85a4350b05feb469/1b0c6841-2a9f-45dd-a3a2-e2a3e60f6600/public"
      : "https://imagedelivery.net/bfb1a679ca6faf7c85a4350b05feb469/1b0c6841-2a9f-45dd-a3a2-e2a3e60f6600/public",
    ogTitle: region === "US"
      ? `${BRAND_PERSON_NAME} | Join My Community - Exclusive Content Access`
      : region === "SPAIN"
      ? `${BRAND_PERSON_NAME} | Únete a Mi Comunidad - Acceso a Contenido Exclusivo`
      : latamTitle,
    ogDescription: region === "US"
      ? "Join my exclusive community! Premium Instagram access, Telegram group, and direct connection. Let's create amazing memories together! ✨"
      : region === "SPAIN"
      ? "¡Únete a mi comunidad exclusiva! Acceso premium a Instagram, grupo de Telegram y conexión directa. ¡Vamos a crear recuerdos increíbles juntos! ✨"
      : siteSettings.description || latamOgFallback,
    canonical: `${BRAND_SITE_URL}/`,
    author: BRAND_PERSON_NAME,
    publishedTime: "2026-01-01T00:00:00.000Z",
    modifiedTime: new Date().toISOString(),
    type: region === "US" ? "profile" : region === "SPAIN" ? "profile" : "website",
    locale: region === "US" ? "en_US" : region === "SPAIN" ? "es_ES" : "es_AR",
    alternateLocale: region === "US" ? "es_AR" : region === "SPAIN" ? "en_US" : "en_US",
    // Homepage: index, follow for crawlers (BaseTemplate and other public templates)
    robots: "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1",
  };

  // Home variants per país detectado por el worker (MX/CO/PE/BR/CL/ES/US).
  // Pisa title/description/keywords/locale del seoConfig con la versión más
  // granular del país real del visitor — Google muestra el snippet específico
  // del país en SERP local. La URL canónica sigue siendo `/`.
  const homeVariant = getHomeVariantForCountry(country);
  if (homeVariant.country !== "INTL") {
    seoConfig.title = homeVariant.title;
    seoConfig.description = homeVariant.description;
    seoConfig.ogTitle = homeVariant.title;
    seoConfig.ogDescription = homeVariant.description;
    seoConfig.locale = homeVariant.locale.replace("-", "_");
  }

  useSEO(seoConfig);

  // Workaround: VITE_GALLERY_PHOTOS (comma-separated URLs) alimenta la galería si está definido
  const envGalleryPhotoItems = useMemo(
    () =>
      getGalleryPhotos().map((item, i) => ({
        id: i + 1,
        type: "photo" as const,
        src: item.src.trim(),
        title: "",
      })),
    []
  );
  const useEnvGalleryPhotos = envGalleryPhotoItems.length > 0;

  // Gallery: Cloudinary/portfolio/site settings; si hay VITE_GALLERY_PHOTOS, tiene prioridad
  const logoUrl = (siteSettings.logoUrl as string)?.trim() || "";

  // Site gallery: fotos Cloudinary o videos Mux desde admin/site-settings.
  // Admite: URL de res.cloudinary.com, public_id Cloudinary, stream.mux.com/ID, image.mux.com/ID.
  // Construido como useMemo para que BaseTemplate reciba una referencia estable.
  const siteGalleryMediaItemsRaw = useMemo(() => {
    const raw = siteSettings.galleryImageUrls ?? [];
    const items: Array<{
      id: number;
      type: "video" | "photo";
      src: string;
      title: string;
      playbackId?: string;
    }> = [];
    let idx = 0;
    for (const entry of raw) {
      if (typeof entry !== "string" || !entry.trim()) continue;
      const t = entry.trim();
      // Mux stream URL → video
      const muxStream = t.match(/stream\.mux\.com\/([^/.?#]+)/);
      if (muxStream) {
        items.push({ id: idx + 1, type: "video", src: "", title: "", playbackId: muxStream[1] });
        idx++;
        continue;
      }
      // Mux image/thumbnail URL → video (extract playback ID)
      const muxImage = t.match(/image\.mux\.com\/([^/.?#]+)/);
      if (muxImage) {
        items.push({ id: idx + 1, type: "video", src: "", title: "", playbackId: muxImage[1] });
        idx++;
        continue;
      }
      // Cloudinary full URL
      if (t.includes("res.cloudinary.com")) {
        if (logoUrl && isSameCloudinaryImage(logoUrl, t)) continue;
        items.push({ id: idx + 1, type: "photo", src: t, title: "" });
        idx++;
        continue;
      }
      // Unknown https:// / http:// (not Cloudinary, not Mux) → skip
      if (t.startsWith("https://") || t.startsWith("http://")) continue;
      // Non-URL string → assume Cloudinary public_id; resolve to full URL
      if (t.length >= 2 && !t.includes(" ") && !t.includes("\n")) {
        const resolved = resolveLogoUrl(t);
        if (!resolved) continue;
        if (logoUrl && isSameCloudinaryImage(logoUrl, resolved)) continue;
        items.push({ id: idx + 1, type: "photo", src: resolved, title: "" });
        idx++;
      }
    }
    return items;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(siteSettings.galleryImageUrls ?? []).join(","), logoUrl]);

  // Cloudinary-only slice for ArgentinaBioTemplate/BioLinkTemplate (legacy photo-only path)
  const siteGalleryUrls = (siteSettings.galleryImageUrls ?? [])
    .filter((u): u is string => typeof u === "string" && u.trim().length > 0 && isCloudinaryImage(u))
    .map((u) => u.trim());
  const galleryWithoutLogo = logoUrl
    ? siteGalleryUrls.filter((u) => !isSameCloudinaryImage(logoUrl, u))
    : siteGalleryUrls;

  const portfolioUrls = portfolioImageUrls
    .filter((u) => u?.trim() && isCloudinaryImage(u))
    .map((u) => u.trim());
  const portfolioWithoutLogo = logoUrl
    ? portfolioUrls.filter((u) => !isSameCloudinaryImage(logoUrl, u))
    : portfolioUrls;

  // Galería para ArgentinaBioTemplate/BioLinkTemplate: env workaround → portfolio → site settings
  const galleryMediaItems = useEnvGalleryPhotos
    ? envGalleryPhotoItems
    : !portfolioLoading && portfolioWithoutLogo.length > 0
      ? (portfolioWithoutLogo.length > 1 ? portfolioWithoutLogo.slice(1) : portfolioWithoutLogo).map((src, i) => ({
          id: i + 1,
          type: "photo" as const,
          src: src.trim(),
          title: "",
        }))
      : galleryWithoutLogo.length > 0
        ? (galleryWithoutLogo.length > 1 ? galleryWithoutLogo.slice(1) : galleryWithoutLogo).map((src, i) => ({
            id: i + 1,
            type: "photo" as const,
            src: src.trim(),
            title: "",
          }))
        : [];

  // BaseTemplate: galería desde VITE_GALLERY_PHOTOS o portfolio (orden aleatorio)
  const brandName = useMemo(
    () => brandDisplayNameFromSiteTitle(siteSettings.title),
    [siteSettings.title],
  );

  // Galería alternativa: máxima prioridad cuando está habilitada, reemplaza portfolio y env
  const altGalleryEnabled = siteSettings.alternativeGalleryEnabled === true;
  const altGalleryUrls = (siteSettings.alternativeGalleryUrls ?? []).filter(
    (u): u is string => typeof u === "string" && u.trim().length > 0
  );

  const baseTemplateGalleryItems = useMemo(() => {
    // Prioridad 1: galería alternativa (curada, orden fijo)
    if (altGalleryEnabled && altGalleryUrls.length > 0) {
      return altGalleryUrls.map((src, i) => ({
        id: i + 1,
        type: "photo" as const,
        src: src.trim(),
        title: "",
      }));
    }
    // Prioridad 2: env workaround (VITE_GALLERY_PHOTOS)
    if (useEnvGalleryPhotos) {
      const shuffled = [...envGalleryPhotoItems].sort(() => Math.random() - 0.5);
      return shuffled.map((item, i) => ({ ...item, id: i + 1 }));
    }
    // Prioridad 3: portfolio en DB (orden aleatorio)
    if (portfolioWithoutLogo.length > 0) {
      const shuffled = [...portfolioWithoutLogo].sort(() => Math.random() - 0.5);
      return shuffled.map((src, i) => ({
        id: i + 1,
        type: "photo" as const,
        src: src.trim(),
        title: "",
      }));
    }
    // Prioridad 4: galería de admin/site-settings — fotos Cloudinary + videos Mux
    // (skipMuxApiFilter={true} ya está en todos los <BaseTemplate> que usan este array)
    if (siteGalleryMediaItemsRaw.length > 0) {
      return siteGalleryMediaItemsRaw.map((item, i) => ({ ...item, id: i + 1 }));
    }
    return [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [altGalleryEnabled, altGalleryUrls.join(","), useEnvGalleryPhotos, envGalleryPhotoItems, portfolioWithoutLogo, siteGalleryMediaItemsRaw]);

  // Gallery nav: mostrar el link "Galería" (#portfolio) cuando hay fotos
  // renderizables O cuando admin configuró URLs en settings (aunque alguna
  // no pase el filtro de Cloudinary/Mux). Sólo se oculta cuando todas las
  // fuentes están vacías — settings sin fotos, portfolio vacío, sin env, sin alt.
  const hasGalleryUrlsInSettings =
    (siteSettings.galleryImageUrls ?? []).some(
      (u) => typeof u === "string" && u.trim().length > 0,
    ) ||
    (altGalleryEnabled && altGalleryUrls.length > 0);
  const showGalleryNavLink =
    baseTemplateGalleryItems.length > 0 ||
    hasGalleryUrlsInSettings ||
    portfolioWithoutLogo.length > 0 ||
    useEnvGalleryPhotos;

  // ── Banner image que se va a renderizar (centralizado) ─────────────────────
  // Misma cascada que cada path computa internamente: settings → alt gallery
  // → portfolio → env. Calcularlo arriba nos permite precargarlo via
  // `useTemplateAssetsReady` antes de mostrar la página, evitando layout
  // shift cuando el template monta y el banner aparece 200-600ms después.
  const resolvedBannerImageUrl = useMemo<string | undefined>(() => {
    if (typeof siteSettings.bannerImageUrl === "string" && siteSettings.bannerImageUrl.trim()) {
      return siteSettings.bannerImageUrl;
    }
    if (altGalleryEnabled && altGalleryUrls.length > 0) return altGalleryUrls[0];
    if (portfolioWithoutLogo.length > 0) return portfolioWithoutLogo[0];
    if (useEnvGalleryPhotos) return envGalleryPhotoItems[0]?.src;
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    siteSettings.bannerImageUrl,
    altGalleryEnabled,
    altGalleryUrls.join(","),
    portfolioWithoutLogo.join(","),
    useEnvGalleryPhotos,
    envGalleryPhotoItems,
  ]);

  // ── Loading unificado: data + banner preload + fonts ────────────────────────
  // Antes cada path (AR dual, intl dual, default base) tenía su propia
  // condición de loading basada solo en `siteSettingsLoading || portfolioLoading`.
  // Ahora delegamos al hook que además espera al banner decodeado y a las
  // fonts del display set, eliminando el flash visual del primer paint.
  // `forceRender` (timeout de 8s ya existente) pisa el hook si la data se
  // demora demasiado — coherente con el comportamiento previo.
  const templateAssetsReady = useTemplateAssetsReady({
    dataLoading: siteSettingsLoading || (!useEnvGalleryPhotos && portfolioLoading),
    bannerImageUrl: resolvedBannerImageUrl,
  });
  const showLoadingScreen = !forceRender && !templateAssetsReady;

  // Render template based on templateName from worker (useTemplateSelection reads __TEMPLATE_CONFIG__)
  const renderTemplate = () => {
    // ── Admin preview override ─────────────────────────────────────────────
    // `?_preview_template=video|us-exclusive` forza el render de un template específico
    // para que el admin pueda verlo desde /admin/preview/dual sin importar su
    // país. Read-only — no toca cookies ni KV. Sin auth check explícito porque
    // sigue el mismo patrón que ?_force_variant=X (admin trust): solo el admin
    // sabe el query param. Si lo descubre un visitor no-admin, lo peor que
    // pasa es ver un template distinto al de su geo (gateway sigue siendo el
    // que decide el componente según country detectado).
    if (typeof window !== "undefined") {
      const previewTemplate = new URLSearchParams(window.location.search).get(
        "_preview_template",
      );
      if (previewTemplate === "us-exclusive") {
        return <USExclusiveLanding />;
      }
      if (previewTemplate === "dual") {
        return <DualCTAVideoLanding />;
      }
      if (previewTemplate === "video") {
        const bannerImageUrlPreview =
          (typeof siteSettings.bannerImageUrl === "string" && siteSettings.bannerImageUrl.trim())
            ? siteSettings.bannerImageUrl
            : useEnvGalleryPhotos ? envGalleryPhotoItems[0]?.src : undefined;
        return (
          <VideoTemplate
            mediaItems={baseTemplateGalleryItems}
            skipMuxApiFilter={true}
            headerUrl={siteSettings.headerUrl}
            logoUrl={siteSettings.logoUrl}
            heroTitle={siteSettings.title}
            heroDescription={siteSettings.description}
            bannerImageUrl={bannerImageUrlPreview}
            bannerDeferred={false}
            galleryDeferred={false}
            sobreMi={siteSettings.sobreMi}
            showSobreMiOnHome={false}
            showPortfolioNavLink={showPortfolioNavLink}
            showGalleryNavLink={showGalleryNavLink}
            showAudiovisualNavLink={showAudiovisualNavLink}
            showSobreMi={false}
            brandName={brandName}
            footerTagline={siteSettings.footerTagline}
          />
        );
      }
    }

    if (templateName === "MaintenanceTemplate") {
      return <MaintenanceTemplate />;
    }

    // Default intl + templateName=DualCTAVideoLanding: la home ANTES era
    // fullscreen vía línea 389. AHORA la promovemos a VideoTemplate vía
    // useDualCta más abajo — el visitor intl ve la home con video Mux +
    // botones + galería BaseTemplate (mejor UX que fullscreen sin nav).
    //
    // CRITICAL: NO podemos requerir showDual=true acá. Si admin no configuró
    // cta_type=dual para el país (showDual=false), el visitor igual debe ver
    // los botones de compra (US/BR/EU sin admin config no pueden quedar sin
    // CTA → bug de "0 conversions"). VideoTemplate los muestra siempre que
    // haya variante asignada o fallback ENV_PLAYBACK_ID configurado.
    //
    // AR ya fue interceptado arriba (heroCta dentro de BaseTemplate). Acá
    // llegamos solo para visitors no-AR.

    // AR Dual CTA: admin activa cta_type="dual" para cualquier scope AR
    // (country, province_ar, city_ar). El Worker emite showDual=true y el
    // templateName puede ser BaseTemplate, ArgentinaBioTemplate,
    // BioTemplatePremium o BioTemplatePremiumSimple — en todos los casos
    // interceptamos aquí y rendimos BaseTemplateMain con "❤️ Ser Exclusivo".
    // hideCta=true (GLOBAL_HIDE_HERO_CTA) desactiva el dual también para AR.
    if (!hideCta && showDual && country === "AR" && templateName === "BaseTemplate") {
      // Gate `templateName === "BaseTemplate"`: el intercept AR dual solo aplica
      // cuando el visitor venía a BaseTemplate. Si admin asignó otro template
      // (VideoTemplate, etc.), respeta esa decisión y deja que el flujo normal
      // siga (esos templates tienen su propio dual completo).
      const arDualLoading =
        !forceRender && (siteSettingsLoading || (!useEnvGalleryPhotos && portfolioLoading));
      if (arDualLoading) {
        return <TemplateLoadingScreen />;
      }
      const bannerImageUrlArDual =
        (typeof siteSettings.bannerImageUrl === "string" && siteSettings.bannerImageUrl.trim())
          ? siteSettings.bannerImageUrl
          : (altGalleryEnabled && altGalleryUrls.length > 0)
            ? altGalleryUrls[0]
            : portfolioWithoutLogo.length > 0
              ? portfolioWithoutLogo[0]
              : useEnvGalleryPhotos ? envGalleryPhotoItems[0]?.src : undefined;
      return (
        <BaseTemplateMain
          mediaItems={baseTemplateGalleryItems}
          skipMuxApiFilter={true}
          headerUrl={siteSettings.headerUrl}
          logoUrl={siteSettings.logoUrl}
          heroTitle={siteSettings.title}
          heroDescription={siteSettings.description}
          bannerImageUrl={bannerImageUrlArDual}
          bannerDeferred={false}
          galleryDeferred={false}
          sobreMi={siteSettings.sobreMi}
          showSobreMiOnHome={false}
          showPortfolioNavLink={showPortfolioNavLink}
          showGalleryNavLink={showGalleryNavLink}
          showAudiovisualNavLink={showAudiovisualNavLink}
          showSobreMi={false}
          brandName={brandName}
          footerTagline={siteSettings.footerTagline}
          heroCta={
            // Label fijo en home AR — la variante A/B (mux/labels/checkout) se
            // resuelve en /ser-miembro (DualCTAVideoLanding), no en BaseTemplate.
            (() => {
              const ctaLabel = t("baseTemplate.arDual.ctaPaid", "Miembro Exclusivo");
              const arVariantSeed = getServerTemplateConfig()?.dualVariant;
              return (
                <Link
                  to="/ser-miembro"
                  onClick={() => {
                    // PostHog: trackea como hero CTA en BaseTemplateArDual para
                    // que aparezca en el funnel cruzado con la página /ser-miembro
                    // (que también trackea page_enter_dual_cta_video al mount).
                    trackBaseTemplateClick({
                      templateName: "BaseTemplateArDual",
                      elementId: "hero_cta_ar_dual_vip",
                      label: ctaLabel,
                      surface: "hero",
                      href: "/ser-miembro",
                      countryCode: country,
                      extra: {
                        cta_source: "ar_dual_home_button",
                        worker_template: templateName || "",
                        // Atribución per-variant: que PostHog pueda filtrar
                        // clicks de hero AR por dual A/B/C sin esperar a
                        // /ser-miembro. Si no hay variante resuelta aún,
                        // los 3 campos quedan undefined (no rompen events).
                        dual_variant_id: arVariantSeed?.id,
                        dual_variant_geo_scope: arVariantSeed?.assignmentScope,
                        dual_variant_geo_code: arVariantSeed?.assignmentGeo,
                      },
                    });
                    // Slack funnel: mismo kind que usa DualCTAVideoLanding/Stripe
                    // checkout para conversión, así el panel agrega clicks de
                    // ambas superficies (AR home + intl dual) bajo un único embudo.
                    try {
                      const ts = detectTrafficSource();
                      const phDiag = getSlackFunnelDiagnosticsExtraPostHog();
                      void fetch("/api/track-funnel-failure", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        keepalive: true,
                        body: JSON.stringify({
                          kind: "cta_click_vip",
                          template: "BaseTemplateArDual",
                          country,
                          page_url: typeof window !== "undefined" ? window.location.href : undefined,
                          extra: {
                            cta_url: "/ser-miembro",
                            cta_source: "ar_dual_home_button",
                            worker_template: templateName || "",
                            worker_language: language,
                            source: ts.source,
                            medium: ts.medium,
                            channel: ts.channel,
                            referrer: (document.referrer || "").slice(0, 300),
                            dual_variant_id: arVariant?.id,
                            ...phDiag,
                          },
                        }),
                      }).catch(() => {});
                    } catch {
                      // best-effort; nunca bloquear la navegación
                    }
                  }}
                  className={[
                    "group flex w-full max-w-[360px] mx-auto items-center justify-center gap-1.5 rounded-full",
                    "bg-amber-400/95 hover:bg-amber-300 active:bg-amber-500",
                    "text-black text-sm font-semibold tracking-tight",
                    "px-4 py-3",
                    "shadow-[0_2px_14px_rgba(251,191,36,0.28)] hover:shadow-[0_2px_18px_rgba(251,191,36,0.38)]",
                    "ring-1 ring-amber-300/40",
                    "transition-all duration-150 no-underline",
                  ].join(" ")}
                  style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent", minHeight: 48, display: "flex" }}
                >
                  <span className="truncate">❤️ {ctaLabel}</span>
                  <span className="text-[11px] opacity-70 transition-transform duration-150 group-hover:translate-x-0.5" aria-hidden>→</span>
                </Link>
              );
            })()
          }
        />
      );
    }

    if (templateName === "ArgentinaBioTemplate") {
      return <ArgentinaBioTemplate mediaItems={galleryMediaItems} />;
    }

    if (templateName === "BioLinkTemplate") {
      return <BioLinkTemplate mediaItems={galleryMediaItems} />;
    }

    if (templateName === "BioTemplatePremium") {
      const currency = getCurrencyForCountry(country);
      const { standardPlan, vipPlan } = getPlansForCurrency(currency, language);
      const { successPath, cancelPath } = getPremiumInvitePaths(currency);
      const bestfriendInstagram =
        getBestFriendInstagramConfigForCountry(country) ??
        (isPremiumSpecialOfferActive() ? BESTFRIEND_INSTAGRAM_CONFIG : null);

      return (
        <BioTemplatePremium
          currency={currency}
          country={country}
          language={language}
          standardPlan={standardPlan}
          vipPlan={vipPlan}
        successPath={successPath}
        cancelPath={cancelPath}
        profileName={BRAND_PERSON_NAME}
        flowName={PREMIUM_INVITE_FLOW_NAME}
        bestfriendInstagram={bestfriendInstagram ?? undefined}
        />
      );
    }

    if (templateName === "BioTemplatePremiumSimple") {
      // Loading exacto: no montamos el template hasta tener la config del admin
      // (GET /api/premium-invite-config). Así `simple_mux_overlay_enabled` y el
      // resto de settings se detectan en el primer render — sin flash de cards
      // antes de que aparezca el overlay.
      if (!premiumSimpleConfigReady) {
        return (
          <div className="fixed inset-0 flex items-center justify-center bg-black">
            <div
              className="h-10 w-10 animate-spin rounded-full border-2 border-white/25 border-t-white"
              aria-hidden
            />
          </div>
        );
      }
      return (
        <BioTemplatePremiumSimple
          language={language}
          country={country}
          freeTelegramPublicUrl={premiumSimpleFreeTelegramUrl}
          premiumInvitePublicConfig={premiumInvitePublicConfig}
        />
      );
    }

    // Default to BaseTemplate — si hay VITE_GALLERY_PHOTOS no esperamos al portfolio
    const baseTemplateLoading =
      !forceRender && (siteSettingsLoading || (!useEnvGalleryPhotos && portfolioLoading));
    if (baseTemplateLoading) {
      return <TemplateLoadingScreen />;
    }

    // Prioridad banner: admin banner → galería alternativa (1ª imagen) → portfolio → env
    const bannerImageUrl =
      (typeof siteSettings.bannerImageUrl === "string" && siteSettings.bannerImageUrl.trim())
        ? siteSettings.bannerImageUrl
        : (altGalleryEnabled && altGalleryUrls.length > 0)
          ? altGalleryUrls[0]
          : portfolioWithoutLogo.length > 0
            ? portfolioWithoutLogo[0]
            : useEnvGalleryPhotos
              ? envGalleryPhotoItems[0]?.src
              : undefined;

    // Mismo criterio que i18n: src/lib/siteGeoPolicy.ts (AR, cookie AR, test AR).
    const workerCfg = getServerTemplateConfig();
    const isArgentinaVisitor = usesSpanishDefaultForSite();
    // Gate `templateName === "BaseTemplate"`: el wishlist intl solo aplica
    // cuando el visitor venía a BaseTemplate. Si admin asignó otro template
    // (VideoTemplate/DualCTAVideoLanding/BioLinkTemplate), respeta esa
    // decisión y deja que el flujo normal siga. Coherente con la regla
    // canónica "Hero CTA per-geo solo en BaseTemplate".
    const useInternationalWishlist =
      !isArgentinaVisitor &&
      country.length === 2 &&
      country !== "UNKNOWN" &&
      workerCfg?.showWishlist === true &&
      templateName === "BaseTemplate";

    // Dual CTA: en `/` para visitors intl (no-AR), renderizamos VideoTemplate.
    // Trigger paths:
    //   1. Admin explicit: `templateName === "VideoTemplate"` (override via
    //      CountryTemplateAdmin) → directo a VideoTemplate.
    //   2. Admin legacy: `templateName === "BaseTemplateInternationalDual"` —
    //      históricamente el wrapper sin video; ahora upgrademos a VideoTemplate
    //      para consistencia (mismo template, sin tocar settings legacy).
    //   3. Default intl: `templateName === "USExclusiveLanding"` (el default
    //      para rest of world en worker map). O "DualCTAVideoLanding".
    //      USExclusive es el fullscreen TikTok EN + Stripe para el mundo.
    //      SIEMPRE usamos el componente si explícito.
    //
    // CRITICAL: Respeta admin override a `BaseTemplate`. Si admin asignó
    // `templateName === "BaseTemplate"` para un país intl (via
    // CountryTemplateAdmin), NO promovemos a VideoTemplate — el visitor ve
    // BaseTemplate normal. Admin lo quiso así.
    //
    // HIERARCHY: GLOBAL_HIDE_HERO_CTA / killSwitch beats todo; phantom hideCta
    // en templates dual self-contained (CountryTemplateAdmin) no bloquea.
    const useDualCta =
      !dualHomeSuppressed &&
      !usesSpanishDefaultForSite() &&
      country.length === 2 &&
      country !== "UNKNOWN" &&
      isIntlDualHomeTemplate(templateName);

    // Intl con showDual:
    //   - templateName === "USExclusiveLanding" (default for rest of world) or
    //     "DualCTAVideoLanding" → render the fullscreen component. Has variant
    //     resolution + Mux + CTAs. For rest of world: USExclusive (EN, USD Stripe).
    //   - templateName === "VideoTemplate" o "BaseTemplateInternationalDual"
    //     (admin override legacy) → fallback al wrapper VideoTemplate que
    //     integra BaseTemplate + galería + sobre mí debajo del hero.
    //
    // Loading: mismo pattern que AR dual — esperamos siteSettings + portfolio
    // antes de renderear el VideoTemplate wrapper (USExclusive/DualCTAVideoLanding es
    // fullscreen y no depende de esas hidrataciones).
    if (useDualCta) {
      // Fullscreen: USExclusiveLanding (rest of world default) or DualCTAVideoLanding
      // or admin asignó BaseTemplateInternationalDual → mismo render fullscreen,
      // reemplaza todo el contenido (no galería debajo). VideoTemplate solo si admin
      // explícitamente eligió ese nombre (galería + video integrados).
      if (templateName === "DualCTAVideoLanding" || templateName === "BaseTemplateInternationalDual") {
        return <DualCTAVideoLanding hostTemplate="DualCTAVideoLanding" />;
      }
      if (templateName === "USExclusiveLanding") {
        return <USExclusiveLanding />;
      }
      // Path admin-override VideoTemplate: con galería + sobre mí debajo del hero.
      const intlDualLoading =
        !forceRender &&
        (siteSettingsLoading || (!useEnvGalleryPhotos && portfolioLoading));
      if (intlDualLoading) {
        return <TemplateLoadingScreen />;
      }
      const bannerImageUrlIntl =
        (typeof siteSettings.bannerImageUrl === "string" && siteSettings.bannerImageUrl.trim())
          ? siteSettings.bannerImageUrl
          : (altGalleryEnabled && altGalleryUrls.length > 0)
            ? altGalleryUrls[0]
            : portfolioWithoutLogo.length > 0
              ? portfolioWithoutLogo[0]
              : useEnvGalleryPhotos ? envGalleryPhotoItems[0]?.src : undefined;
      return (
        <VideoTemplate
          mediaItems={baseTemplateGalleryItems}
          skipMuxApiFilter={true}
          headerUrl={siteSettings.headerUrl}
          logoUrl={siteSettings.logoUrl}
          heroTitle={siteSettings.title}
          heroDescription={siteSettings.description}
          bannerImageUrl={bannerImageUrlIntl}
          bannerDeferred={false}
          galleryDeferred={false}
          sobreMi={siteSettings.sobreMi}
          showSobreMiOnHome={false}
          showPortfolioNavLink={showPortfolioNavLink}
          showGalleryNavLink={showGalleryNavLink}
          showAudiovisualNavLink={showAudiovisualNavLink}
          showSobreMi={false}
          brandName={brandName}
          footerTagline={siteSettings.footerTagline}
        />
      );
    }

    if (useInternationalWishlist) {
      return (
        <BaseTemplateInternational
          mediaItems={baseTemplateGalleryItems}
          skipMuxApiFilter={true}
          headerUrl={siteSettings.headerUrl}
          logoUrl={siteSettings.logoUrl}
          heroTitle={siteSettings.title}
          heroDescription={siteSettings.description}
          bannerImageUrl={bannerImageUrl}
          bannerDeferred={false}
          galleryDeferred={false}
          sobreMi={siteSettings.sobreMi}
          showSobreMiOnHome={false}
          showPortfolioNavLink={showPortfolioNavLink}
          showGalleryNavLink={showGalleryNavLink}
          showAudiovisualNavLink={showAudiovisualNavLink}
          showSobreMi={false}
          brandName={brandName}
          footerTagline={siteSettings.footerTagline}
        />
      );
    }

    // Selección de componente base:
    //   · Argentina (isArgentinaVisitor) → BaseTemplate.tsx (BaseTemplateMain),
    //     el diseño limpio/claro. Cubre AR real (config/cookie/edge) y el
    //     fallback Argentina-first (UNKNOWN en el host de marca).
    //   · Resto (fallthrough intl con override BaseTemplate) → BaseTemplateBlack
    //     (diseño negro), sin cambios respecto al comportamiento previo.
    // Ambos comparten la misma interfaz TemplateProps, así que las props son
    // idénticas. La detección de AR ya está garantizada antes del paint: el
    // splash (useLoadingGate + useTemplateAssetsReady) se sostiene hasta que
    // el worker inyectó __TEMPLATE_CONFIG__ (país resuelto) + banner + fonts.
    const FinalBaseTemplate = isArgentinaVisitor ? BaseTemplateMain : BaseTemplate;
    return (
      <FinalBaseTemplate
        mediaItems={baseTemplateGalleryItems}
        skipMuxApiFilter={true}
        headerUrl={siteSettings.headerUrl}
        logoUrl={siteSettings.logoUrl}
        heroTitle={siteSettings.title}
        heroDescription={siteSettings.description}
        bannerImageUrl={bannerImageUrl}
        bannerDeferred={false}
        galleryDeferred={false}
        sobreMi={siteSettings.sobreMi}
        showSobreMiOnHome={false}
        showPortfolioNavLink={showPortfolioNavLink}
        showGalleryNavLink={showGalleryNavLink}
        showAudiovisualNavLink={showAudiovisualNavLink}
        showSobreMi={false}
        brandName={brandName}
        footerTagline={siteSettings.footerTagline}
        // Hero CTA global de site-settings (heroCta_* gestionable en
        // /admin/site-settings). Worker SSR resuelve URL membership por país
        // (Stripe vs MP via MEMBERSHIP_LATAM). Sin esto, los settings de
        // heroCta_* quedaban invisibles al visitor (bug fix 2026-06).
        adminCta={
          workerCfg?.heroCta
            ? {
                label: workerCfg.heroCta.label,
                url: workerCfg.heroCta.url,
                emoji: workerCfg.heroCta.emoji,
                type: workerCfg.heroCta.type,
              }
            : undefined
        }
      />
    );
  };

  // Antes: renderTemplate() inmediato — provocaba flash visual del template
  // sin banner (banner cargaba 200-600ms después del primer paint).
  // Ahora: useLoadingGate maneja el lifecycle del splash con MIN_SHOW_MS=350
  // para evitar flicker en cargas rápidas + FADE_OUT_MS=280 para transición
  // smooth. El content renderiza en paralelo durante el fade-out para que el
  // visitor vea el template asomar suave desde detrás del splash.
  const { splashVisible, fadingOut, contentReady } = useLoadingGate(showLoadingScreen);

  return (
    <>
      {contentReady && (
        <Suspense fallback={<TemplateLoadingScreen />}>
          {renderTemplate()}
        </Suspense>
      )}
      {splashVisible && <TemplateLoadingScreen fadingOut={fadingOut} />}
      <ArgentinaProvinceAdmin />
    </>
  );
};

export default Index;